import { call, put, takeLatest } from 'redux-saga/effects';
import { addToRootSaga } from 'store';

import { getNewDate, displayDate, FILENAME_DATE_FORMAT } from 'utils/date';
import { addPositiveToast, addNegativeToastWithErrorId } from 'modules/toastContainer';
import {
  archiveCampaigns,
  copyCampaign,
  exportCampaignToCSV,
  getCampaignMetrics,
  unarchiveCampaigns,
} from 'api/CampaignIndexAPI';

import {
  archiveCampaignsResponse,
  copyCampaignResponse,
  getCampaignMetricsResponse,
  unarchiveCampaignsResponse,
} from './actions';
import * as actionTypes from './actionTypes';

export function* copyCampaignSaga({
  campaignId,
  destinationFolderId,
  refetch,
}: Record<string, any>) {
  try {
    yield call(copyCampaign, campaignId, destinationFolderId);
    yield put(copyCampaignResponse());
    yield put(addPositiveToast('Successfully copied campaign'));
    refetch();
  } catch (error) {
    const errorMessage = 'Unable to copy campaign';
    yield put(addNegativeToastWithErrorId(errorMessage, 'copyCampaignSaga', error));
  }
}

export function* archiveCampaignsSaga({
  campaignIds,
  refetch,
}: Record<string, any>): Generator<any, any, any> {
  try {
    const response = yield call(archiveCampaigns, campaignIds);
    yield put(archiveCampaignsResponse());
    if (response.success.length) {
      yield put(
        addPositiveToast(`Successfully archived campaign${response.success.length > 1 ? 's' : ''}`),
      );
    }
    if (response.fail.length) {
      const errorMessage = `Unable to archive campaign${response.fail.length > 1 ? 's' : ''}`;
      yield put(addNegativeToastWithErrorId(errorMessage, 'archiveCampaignsSaga', response.fail));
    }
    refetch();
  } catch (error) {
    const errorMessage = `Unable to archive campaign${campaignIds.length > 0 ? 's' : ''}`;
    yield put(addNegativeToastWithErrorId(errorMessage, 'archiveCampaignsSaga', error));
  }
}

export function* unarchiveCampaignsSaga({ campaignIds, refetch }: Record<string, any>) {
  try {
    yield call(unarchiveCampaigns, campaignIds);
    yield put(unarchiveCampaignsResponse());
    yield put(
      addPositiveToast(`Successfully unarchived campaign${campaignIds.length > 0 ? 's' : ''}`),
    );
    refetch();
  } catch (error) {
    const errorMessage = `Unable to unarchive campaign${campaignIds.length > 0 ? 's' : ''}`;
    yield put(addNegativeToastWithErrorId(errorMessage, 'unarchiveCampaignsSaga', error));
  }
}

export function* exportCampaignToCSVSaga({
  campaignIds,
  projectName,
}: Record<string, any>): Generator<any, any, any> {
  try {
    const fileText = yield call(exportCampaignToCSV, campaignIds);

    // This block creates a link that will download a file
    const element = document.createElement('a');
    // Create File from returned text
    const file = new Blob([fileText], { type: 'text/html' });
    element.href = URL.createObjectURL(file);
    // Set name
    element.download = `${projectName}_iterable_campaigns_report_${displayDate(
      getNewDate(),
      FILENAME_DATE_FORMAT,
    )}.csv`;
    document.body.appendChild(element); // Required for this to work in FireFox
    // downloads file
    element.click();
    document.body.removeChild(element);
    yield put(addPositiveToast('Exporting campaigns'));
  } catch (error) {
    const errorMessage = 'Unable to export CSVs';
    yield put(addNegativeToastWithErrorId(errorMessage, 'exportCampaignToCSVSaga', error));
  }
}

export function* getCampaignMetricsSaga({
  campaignIds,
}: Record<string, any>): Generator<any, any, any> {
  try {
    const result = yield call(getCampaignMetrics, campaignIds);
    yield put(getCampaignMetricsResponse(result));
  } catch (error) {
    const errorMessage = 'Unable to fetch certain campaign metrics';

    yield put(addNegativeToastWithErrorId(errorMessage, 'getCampaignMetricsSaga', error));
    yield put(getCampaignMetricsResponse()); // clear loading state
  }
}

addToRootSaga([
  takeLatest(actionTypes.COPY_CAMPAIGN_REQUEST, copyCampaignSaga),
  takeLatest(actionTypes.ARCHIVE_CAMPAIGNS_REQUEST, archiveCampaignsSaga),
  takeLatest(actionTypes.EXPORT_CAMPAIGN_TO_CSV_REQUEST, exportCampaignToCSVSaga),
  takeLatest(actionTypes.GET_CAMPAIGN_METRICS_REQUEST, getCampaignMetricsSaga),
  takeLatest(actionTypes.UNARCHIVE_CAMPAIGNS_REQUEST, unarchiveCampaignsSaga),
]);
